/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */

import {
  IAppState,
  IListComponentModel,
  IMediaListModel,
  IMediaModel,
  MediaStore,
  SourceType,
  CellType,
  DeviceHelper,
  AssetType,
} from "@nf/common";
import cx from "classnames";
import { useTranslation } from "next-i18next";
import { useDispatch, useSelector } from "react-redux";
import Slider from "react-slick";
import { IListComponentProps } from "../../types";
import dynamic from "next/dynamic";

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { InView } from "react-intersection-observer";
import { isServerSideRender } from "@nf/helpers";
import { Settings } from "react-slick";
import "slick-carousel/slick/slick.css";
import styles from "./ListComponentHorizontal.module.scss";
import { useListItemCount } from "../../hooks";
import { useMediaForKids } from "../../../../../hooks";
import { ListComponentItem } from "../ListComponentItem";
import { LinkToPlaylist } from "./components/LinkToPlaylist";

const SeeAllButton = dynamic(() => import("./components/SeeAllButton"));

const ListComponentArrow = dynamic(() => import("../ListComponentArrow"));

const ListComponentHorizontalSSR = dynamic(
  () => import("../ListComponentItemSSR/ListComponentHorizontalSSR")
);

const useSourceSelector = (
  component: IListComponentModel
): IMediaListModel | undefined => {
  const mediaList =
    useSelector((state: IAppState) => state.media.mediaList) || {};
  if (component.SourceId) {
    return mediaList[component.SourceId];
  }

  if (component.MediaList) {
    return {
      SourceType: SourceType.MediaList,
      Entities: component.MediaList,
      TotalCount: component.MediaList.length,
    };
  }
};

const useGetMediaList = () => {
  const dispatch = useDispatch();
  return useCallback(
    (
      mediaListId: string | number,
      sourceUrl?: string,
      sourceType?: SourceType
    ) =>
      dispatch(
        MediaStore.Actions.getMediaList({
          MediaListId: mediaListId,
          SourceUrl: sourceUrl,
          SourceType: sourceType,
          PageNumber: 1,
          PageSize: 12,
        })
      ),
    [dispatch]
  );
};

export interface IListComponentHorizontalProps extends IListComponentProps {
  className?: string;
  placeholder?: React.ReactElement;
  showHeader?: boolean;
  settings?: Settings;

  initialActiveIndex?: number;
  activeIndex?: number;
  hoverIndex?: number;
  onItemClick?: (index?: number, item?: IMediaModel) => void;
  onItemMouseEnter?: (index?: number, item?: IMediaModel) => void;
  onItemMouseLeave?: (index?: number, item?: IMediaModel) => void;
}

export const ListComponentHorizontal = ({
  className,
  component,
  loading: isLoading,
  placeholder,
  readOnly,
  showHeader = true,
  settings,
  initialActiveIndex,
  activeIndex,
  hoverIndex,
  onItemClick,
  onItemMouseEnter,
  onItemMouseLeave,
}: IListComponentHorizontalProps) => {
  const { t } = useTranslation("translations");
  const source = useSourceSelector(component);
  const itemCount = useListItemCount(component);
  const getMediaList = useGetMediaList();
  const isDesktopSize = !isServerSideRender && window.innerWidth > 850;
  const [isListFocused, setIsListFocused] = useState(false);
  const [sliderTouched, setSliderTouched] = useState(false);
  const [showLefArrow, setShowLefArrow] = useState(false);
  const [sliderTouchedCount, setSliderTouchedCount] = useState(0);
  const IsHighlightComponent = component.CellType === CellType.Highlights;
  const listClassName = cx(styles.list, className);
  const isSafariMobile = DeviceHelper.isSafari() && !isDesktopSize;
  const [isInView, setIsInView] = useState(false);
  const items = useMediaForKids(source?.Entities);
  const isListNotEmpty = !!items?.length;
  const isListLoading = source?.IsLoading || isLoading;
  const isEntitiesShort = items && items.length <= itemCount;

  // Bring the initially active item into view
  const scrollToInitialActiveItem = useCallback((slider: Slider | null) => {
    if (!slider || !initialActiveIndex || !items?.length) {
      return;
    }

    const slide = Math.min(initialActiveIndex, items.length - itemCount);

    if (slide > 0) {
      slider.slickGoTo(slide, true);
    }
  }, []);

  const inViewGetMediaList = useCallback(
    (inView: boolean) => {
      if (inView && component.SourceId && getMediaList) {
        getMediaList(
          component.SourceId,
          component.SourceUrl,
          component.SourceType
        );
      }
      setIsInView(inView);
    },
    [getMediaList, component.SourceId]
  );

  useEffect(() => {
    if (isInView && !source && component.SourceId) {
      getMediaList(
        component.SourceId,
        component.SourceUrl,
        component.SourceType
      );
    }
  }, [isInView, source, getMediaList, component.SourceId]);

  const renderItems = useMemo(() => {
    if (source && items && isListNotEmpty) {
      return items.map((media: IMediaModel, index: number) => {
        if (media.MediaTypeCode !== AssetType.Intro) {
          return (
            <ListComponentItem
              sourceType={source.SourceType}
              key={`${media.Id}`}
              index={index}
              cellType={component.CellType}
              media={media}
              isPlaceholder={false}
              isActive={activeIndex === index}
              isHovered={hoverIndex === index}
              isRowActive={activeIndex !== undefined}
              isRowHovered={hoverIndex !== undefined}
              withProgress={source.SourceType === SourceType.ContinueWatching}
              readOnly={readOnly}
              cutomListItem={component.CutomListItem}
              query={component.QueryParams}
              onClick={onItemClick}
              onMouseEnter={onItemMouseEnter}
              onMouseLeave={onItemMouseLeave}
            />
          );
        }
      });
    }

    return (
      placeholder ||
      Array(itemCount + 1)
        .fill(undefined)
        .map((_, idx) => (
          <ListComponentItem
            key={`placeholder_${idx}`}
            cellType={component.CellType}
            isPlaceholder
          />
        ))
    );
  }, [source, items, isListNotEmpty]);

  const handleSliderAfterTouched = () => {
    setSliderTouched(true);
  };

  const handleSliderBeforeTouched = () => {
    setShowLefArrow(true);
  };

  useEffect(() => {
    if (component.CellType === CellType.Highlights) return;
    if (source && source.Entities.length / itemCount < 2) {
      setSliderTouchedCount(sliderTouchedCount + 1);
    }
  }, [sliderTouched]);

  const arrowContainer = useCallback(
    (arrowDirection: "left" | "right") => {
      const isLeftArrow = arrowDirection === "left";

      if (isLeftArrow && !showLefArrow && !IsHighlightComponent) {
        return <></>;
      }

      if (!isListFocused) {
        return <></>;
      }

      return (
        <ListComponentArrow
          direction={arrowDirection}
          gradient={!IsHighlightComponent}
        />
      );
    },
    [isListFocused, showLefArrow]
  );

  const renderHeader = useMemo(() => {
    const title = t(component.TitleTranslationKey || "", component.Title);
    const hasRedirectLink = !readOnly && component.SourceId;

    const isListReady = isListNotEmpty;
    const isListReadyToBeRedirected =
      isListReady && hasRedirectLink && !isEntitiesShort;

    const shouldLinkToPlaylist = isListReadyToBeRedirected && title;
    const shouldDisplaySeeAll =
      isListReadyToBeRedirected && (isListFocused || !isDesktopSize);

    if (title || (component.SourceId && !isServerSideRender)) {
      return (
        <header className={styles.titleContainer}>
          {shouldLinkToPlaylist ? (
            <LinkToPlaylist
              sourceId={component.SourceId}
              sourceType={component.SourceType}
              cellType={component.CellType}
            >
              {title}
            </LinkToPlaylist>
          ) : (
            title
          )}
          {shouldDisplaySeeAll && (
            <SeeAllButton
              sourceId={component.SourceId}
              sourceType={component.SourceType}
              cellType={component.CellType}
              query={component.QueryParams}
            />
          )}
        </header>
      );
    }
  }, [
    component.TitleTranslationKey,
    component.SourceId,
    readOnly,
    isListNotEmpty,
    isListFocused,
    isDesktopSize,
    isEntitiesShort,
  ]);

  if (source && !isListLoading && !source.TotalCount) {
    return null;
  }

  return (
    <InView
      as="div"
      id={`list-${component.Id}`}
      className={listClassName}
      rootMargin="25% 0px"
      triggerOnce
      onChange={inViewGetMediaList}
    >
      <div
        className={styles.container}
        onMouseOver={isSafariMobile ? undefined : () => setIsListFocused(true)}
        onMouseLeave={
          isSafariMobile ? undefined : () => setIsListFocused(false)
        }
      >
        {showHeader && renderHeader}

        <ListComponentHorizontalSSR
          mediaListSSR={component.MediaListSSR}
          mediaList={component.MediaList}
          cellType={component.CellType}
        />

        {!isServerSideRender && (
          <Slider
            className={cx(
              { ["circle-list"]: component.CellType === CellType.Circle },
              { ["cover-list"]: component.CellType === CellType.Cover }
            )}
            ref={scrollToInitialActiveItem}
            {...settings}
            slidesToShow={itemCount}
            slidesToScroll={
              sliderTouchedCount === 1 ? itemCount - 1 : itemCount
            }
            draggable={false}
            infinite={
              (sliderTouched || IsHighlightComponent) && !isEntitiesShort
            }
            afterChange={handleSliderAfterTouched}
            beforeChange={handleSliderBeforeTouched}
            prevArrow={arrowContainer("left")}
            nextArrow={arrowContainer("right")}
          >
            {renderItems}
          </Slider>
        )}
      </div>
    </InView>
  );
};

export default ListComponentHorizontal;
